import React from "react";
import type { ApolloClient } from "@apollo/client";
import { debounce, isEmpty } from "lodash";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card, Col, Form, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FiFolder } from "react-icons/fi";
import moment from "moment";

import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { getPaginatedEmployerOrders } from "actions/adminActions";
import Alert from "components/Alert";
import Button from "components/Button";
import InfoTable from "components/InfoTable";
import SimpleModal from "components/SimpleModal";
import IconSpinner from "components/IconSpinner";
import IconTable from "components/IconTable";
import IconTableHeader from "components/IconTableHeader";
import Paginator from "components/Paginator";
import IconMessageCard from "components/IconMessageCard";
import { copyTextToClipboard } from "utils/dom";
import {
  isProduction,
  productionEmployerOrigin,
  stagingEmployerOrigin,
} from "utils/determineSite";
import type { RootState } from "types/state";
import type { IconTableColumn } from "components/IconTable";
import type { GetEmployerOrdersQuery } from "types/graphql";

type EmployerOrderRow =
  GetEmployerOrdersQuery["getEmployerOrders"]["rows"][number];

// only a PROVISIONED code can be used to sign up, see the employer order statuses in icon-rest-api
const STATUS_LABELS: Record<string, string> = {
  PENDING: "Processing",
  PROVISIONED: "Ready",
  FAILED: "Failed",
  LINKED: "Signed Up",
};

export const getSignupUrl = (signupCode: string) => {
  const origin = isProduction()
    ? productionEmployerOrigin
    : stagingEmployerOrigin;
  return `${origin}/?signupCode=${signupCode}`;
};

const formatDate = (date?: unknown) =>
  date ? moment(date as string).format("YYYY-MM-DD") : "";

// everything about the order that isn't a table column, shown from "View Metadata"
const METADATA_FIELDS = [
  { key: "stripeCustomerId", label: "Stripe Customer ID" },
  { key: "ein", label: "EIN" },
  { key: "phone", label: "Phone" },
  { key: "priceLookupKey", label: "Price" },
  { key: "setupFee", label: "Setup Fee" },
  { key: "billingTerm", label: "Fee Schedule" },
  { key: "discount", label: "Coupon" },
  { key: "billingDeferral", label: "Billing Delay" },
  { key: "completedDateTime", label: "Signed", format: formatDate },
  { key: "createdAt", label: "Received", format: formatDate },
  { key: "docusignEnvelopeId", label: "DocuSign Envelope ID" },
  { key: "id", label: "Order ID" },
];

const isFetching = createLoadingSelector(adminConstants.GET_EMPLOYER_ORDERS);
const errorSelector = createErrorSelector(adminConstants.GET_EMPLOYER_ORDERS);

const mapStateToProps = (state: RootState) => {
  const { results, totalRows } = state.admin.employerOrders;

  return {
    // admin slice models results as unknown[]
    employerOrders: results as EmployerOrderRow[],
    totalRows,
    isFetching: isFetching(state),
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  getPaginatedEmployerOrders,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = WithApolloClient<ConnectedProps<typeof connector>>;

interface State {
  limit: number;
  offset: number;
  search: string;
  initialFetching: boolean;
  page: number;
  metadataOrder: EmployerOrderRow | null;
}

class EmployerOrders extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      limit: 50,
      offset: 0,
      search: "",
      initialFetching: true,
      page: 0,
      metadataOrder: null,
    };
  }

  componentDidMount() {
    void this._getPaginatedEmployerOrders().then(() => {
      this.setState({ initialFetching: false });
    });
  }

  _onPageChange = ({ selected }: { selected: number }) => {
    const offset = selected * this.state.limit;

    this.setState({ page: selected, offset }, () =>
      this._getPaginatedEmployerOrders()
    );
  };

  _onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ search: e.target.value, offset: 0, page: 0 }, () =>
      this._getPaginatedEmployerOrders()
    );
  };

  _debounceOnChange = debounce(this._onSearchChange, 500);

  _getPaginatedEmployerOrders = () => {
    return this.props.getPaginatedEmployerOrders(
      this.props.client as ApolloClient<object>,
      {
        limit: this.state.limit,
        offset: this.state.offset,
        search: this.state.search,
      }
    );
  };

  _columns = (): Array<IconTableColumn<EmployerOrderRow>> => [
    {
      label: "Business Name",
      key: "businessName",
    },
    {
      label: "Status",
      key: "status",
      customComponent: (props) => (
        <span>
          {STATUS_LABELS[props.status] || props.status}
          {props.failureReason && (
            <div style={{ fontSize: 12, color: "#B12121" }}>
              {props.failureReason}
            </div>
          )}
        </span>
      ),
    },
    {
      label: "Sign-Up Link",
      customComponent: (props) =>
        props.status === "PROVISIONED" ? (
          <button
            type="button"
            className="icon-link btn btn-link p-0"
            onClick={() => copyTextToClipboard(getSignupUrl(props.signupCode))}
          >
            Copy Link
          </button>
        ) : null,
    },
    {
      label: "Signup Code",
      key: "signupCode",
    },
    {
      label: "Company",
      key: "companyName",
      customComponent: (props) =>
        props.companyId ? (
          <Link to={`/employers/${props.companyId}`} className="icon-link">
            {props.companyName || "View Company"}
          </Link>
        ) : null,
    },
    {
      label: "Billing Email",
      key: "billingContactEmail",
    },
    {
      label: "Metadata",
      customComponent: (props) => (
        <Button
          color="action"
          name="action"
          size="sm"
          btnLabel="View Metadata"
          onClick={() => this.setState({ metadataOrder: props })}
        />
      ),
    },
  ];

  render() {
    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }

    const data = this.props.employerOrders;
    const noOrders = isEmpty(data);

    return (
      <div id="employer-orders">
        <h2 style={{ color: "#0a2540" }}>Signup Codes</h2>
        {this.state.metadataOrder && (
          <SimpleModal
            show={true}
            size="lg"
            onClose={() => this.setState({ metadataOrder: null })}
            title={`${this.state.metadataOrder.businessName || "Order"} Metadata`}
            buttonText="close"
          >
            <InfoTable
              metadata={METADATA_FIELDS}
              data={this.state.metadataOrder}
            />
          </SimpleModal>
        )}
        <Card>
          {this.props.isFetching && <IconSpinner centered />}
          <Row>
            <Col md={6}>
              <div
                style={{ paddingLeft: 15, paddingTop: 15 }}
                className="search-bar"
              >
                <Form.Label>
                  Search by business name, signup code or envelope id
                </Form.Label>
                <Form.Control
                  name="search"
                  placeholder="search"
                  onChange={this._debounceOnChange}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              {this.props.error && (
                <div style={{ margin: 12 }}>
                  <Alert type="error" msg={this.props.error} />
                </div>
              )}
              {!this.props.error && !noOrders && (
                <>
                  <IconTableHeader
                    tableHeader="Signup Codes"
                    tableCountLabel="Orders"
                    tableCount={this.props.totalRows}
                  />
                  <IconTable columns={this._columns()} data={data} />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      paddingBottom: 15,
                    }}
                  >
                    <Paginator
                      onChange={this._onPageChange}
                      pageCount={this.props.totalRows / this.state.limit}
                      page={this.state.page}
                    />
                  </div>
                </>
              )}
              {!this.props.error && noOrders && (
                <div style={{ margin: 12 }}>
                  <IconMessageCard
                    header="No Signup Codes"
                    subheader={
                      this.state.search
                        ? "No signed order forms match your search"
                        : "Signup codes appear here once an order form is signed in DocuSign"
                    }
                    icon={<FiFolder color="white" stroke="#60A4BF" size={16} />}
                  />
                </div>
              )}
            </Col>
          </Row>
        </Card>
      </div>
    );
  }
}

export default connector(withApollo<Props>(EmployerOrders));
